/**
 * Input/output contract for `bulkResolveAttendees` — Phase C of the
 * resolve-and-propose architecture.
 *
 * Mirrors the input shape of `bulkInviteToEvent` (eventId + emails). The
 * resolve callable does NOT write; it returns per-email proposals so the
 * Attendees import dialog can surface ADR-057 identity matches before
 * `bulkInviteToEvent` is called to commit the writes.
 *
 * Today's `ExcelAttendeeImportDialog` does its own in-dialog dedupe
 * against the global users + contacts collections via the
 * `useAllUsers` / `useAllContacts` snapshots. That snapshot can be
 * stale relative to a contact created seconds ago in another tab, and
 * it doesn't surface prior-event affiliations. Moving the
 * classification server-side via `bulkResolveAttendees` removes that
 * race + makes the prior-events surface visible.
 */

import { z } from 'zod';

export const bulkResolveAttendeesInputSchema = z
  .object({
    eventId: z.string().min(1),
    /** Emails to classify. Normalized + deduped server-side. Capped at 1000. */
    emails: z.array(z.string().min(1)).min(1).max(1000),
  })
  .strict();

export type BulkResolveAttendeesInput = z.infer<
  typeof bulkResolveAttendeesInputSchema
>;

export type BulkResolveAttendeesRowStatus =
  /** Email matches a user OR contact globally; not yet a member of this event. */
  | 'new'
  /** Email matches a user with an active membership in this event already. */
  | 'already_member'
  /** Email has a pending sent invitation to this event already. */
  | 'already_invited'
  /** Email matches neither a user nor a contact. Caller would need to createContact first. */
  | 'not_found'
  | 'failed';

export interface BulkResolveAttendeesProposal {
  /** Tag the surface type so the dialog can render the right chip. */
  source: 'existing_user' | 'existing_contact';
  userId?: string;
  contactId?: string;
  displayName: string;
  photoURL: string | null;
  /** Prior-event affiliations for the matched person. */
  priorEventNames: string[];
  /** When status === 'already_invited': the existing invitation doc id. */
  existingInvitationId?: string;
}

export interface BulkResolveAttendeesRowResult {
  /** Original email as the caller passed it. */
  email: string;
  /** Lowercased / normalized form, surfaced for de-dupe in the dialog. */
  normalizedEmail: string;
  status: BulkResolveAttendeesRowStatus;
  /** Populated for every non-not_found / non-failed status. */
  proposal?: BulkResolveAttendeesProposal;
  /** Populated when status === 'failed'. */
  message?: string;
}

export interface BulkResolveAttendeesResponse {
  success: true;
  results: BulkResolveAttendeesRowResult[];
  summary: {
    new: number;
    alreadyMember: number;
    alreadyInvited: number;
    notFound: number;
    failed: number;
  };
}
